/**
 * TaskStore — the tasks feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY tasks module that talks to the API, and it
 * does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Task>('tasks')` + `api.identityProvider`). It never imports
 * ApiClient or calls fetch. React hooks call this store; pages call the hook.
 *
 * Persistence model: each task is one document with id `task_<uuid>`. Every
 * renderable field (roomId, title, description, status, assignee, priority,
 * estimateId, tags, createdBy, updatedAt, deleted) is mirrored onto the DDB
 * pointer row's index attributes so a room's board/list renders directly from
 * the pointer rows — WITHOUT hydrating S3 bodies per task.
 *
 * Room scoping: {@link listByRoom} issues a roomId-filtered `listMetaInfo`
 * query (roomId is stored as an index attribute) and, defensively, re-filters
 * client-side by collection + roomId + not-deleted in case the backend does not
 * honor the filter expression.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { ListMetaInfoQuery } from '../../api';
import { KnowledgeBaseApi } from '../../api';
import type { Blocker, BoardStatus, KbResearch, Task, TaskDraft } from './types';
import { isBoardStatus, isTaskStatus, isTaskType, taskDocId } from './taskModel';

export const TASKS_COLLECTION = 'tasks';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  roomId: 'roomId',
  title: 'title',
  description: 'description',
  status: 'status',
  assignee: 'assignee',
  priority: 'priority',
  estimateId: 'estimateId',
  tags: 'tags',
  createdBy: 'createdBy',
  updatedAt: 'updatedAt',
  deleted: 'deleted',
  // ---- Sprint Board index attributes ----
  type: 'type',
  sprintId: 'sprintId',
  boardStatus: 'boardStatus',
  blocked: 'blocked',
  blocker: 'blocker',
  storyPoints: 'storyPoints',
  effortDays: 'effortDays',
  confidence: 'confidence',
  goalId: 'goalId',
  workstreamId: 'workstreamId',
  serviceId: 'serviceId',
  owner: 'owner',
  context: 'context',
  parentTaskId: 'parentTaskId',
  actualEffortDays: 'actualEffortDays',
  completionNote: 'completionNote',
  adhoc: 'adhoc',
  addedToSprintAt: 'addedToSprintAt',
} as const;

/** Sentinel stored on the pointer row for a backlog (unsprinted) task. */
const BACKLOG_SPRINT = '';

/** Arguments for grounding a task against the knowledge base (RetrieveAndGenerate). */
export interface ResearchTaskArgs {
  /** The enriched question to send (built by {@link buildResearchQuestion}). */
  question: string;
}

export class TaskStore {
  private readonly docs: DocumentStore<Task>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Task>(TASKS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(task: Task): StringMap {
    return {
      [ROW.roomId]: task.roomId,
      [ROW.title]: task.title,
      [ROW.description]: task.description ?? '',
      [ROW.status]: task.status,
      [ROW.assignee]: task.assignee ?? '',
      [ROW.priority]: task.priority ?? '',
      [ROW.estimateId]: task.estimateId ?? '',
      [ROW.tags]: JSON.stringify(task.tags ?? []),
      [ROW.createdBy]: task.createdBy ?? '',
      [ROW.updatedAt]: task.updatedAt ?? '',
      [ROW.deleted]: task.deleted ? 'true' : 'false',
      // Sprint Board fields.
      [ROW.type]: task.type ?? 'Task',
      [ROW.sprintId]: task.sprintId ?? BACKLOG_SPRINT,
      [ROW.boardStatus]: task.boardStatus ?? 'TODO',
      [ROW.blocked]: task.blocked ? 'true' : 'false',
      [ROW.blocker]: task.blocker ? JSON.stringify(task.blocker) : '',
      [ROW.storyPoints]: task.storyPoints != null ? String(task.storyPoints) : '',
      [ROW.effortDays]: task.effortDays != null ? String(task.effortDays) : '',
      [ROW.confidence]: task.confidence != null ? String(task.confidence) : '',
      [ROW.goalId]: task.goalId ?? '',
      [ROW.workstreamId]: task.workstreamId ?? '',
      [ROW.serviceId]: task.serviceId ?? '',
      [ROW.owner]: task.owner ?? '',
      [ROW.context]: task.context ?? '',
      [ROW.parentTaskId]: task.parentTaskId ?? '',
      [ROW.actualEffortDays]:
        task.actualEffortDays != null ? String(task.actualEffortDays) : '',
      [ROW.completionNote]: task.completionNote ?? '',
      // adhoc is stored as a string flag so scope metrics can scan the pointer row.
      [ROW.adhoc]: task.adhoc ? 'true' : 'false',
      [ROW.addedToSprintAt]: task.addedToSprintAt ?? '',
    };
  }

  /** Parses an optional numeric pointer-row attribute (empty → undefined). */
  private parseNumberAttr(raw: string | undefined): number | undefined {
    if (raw == null || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }

  /** Safely parses the JSON-encoded blocker attribute. */
  private parseBlockerAttr(raw: string | undefined): Blocker | undefined {
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof parsed.reason === 'string') {
        return parsed as Blocker;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  /** Safely parses the JSON-encoded tags attribute. */
  private parseTagsAttr(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : [];
    } catch {
      return [];
    }
  }

  /**
   * Reconstructs a full Task from a pointer row. Every field is mirrored onto
   * the row, so the board/list never needs an S3 read.
   */
  private toTask(row: StringMap): Task {
    const status = row[ROW.status] ?? '';
    const boardStatusRaw = row[ROW.boardStatus] ?? '';
    const typeRaw = row[ROW.type] ?? '';
    const sprintIdRaw = row[ROW.sprintId];
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      roomId: row[ROW.roomId] ?? '',
      title: row[ROW.title] ?? '',
      description: row[ROW.description] ?? '',
      status: isTaskStatus(status) ? status : 'BACKLOG',
      assignee: row[ROW.assignee] || undefined,
      priority: (row[ROW.priority] as Task['priority']) || undefined,
      estimateId: row[ROW.estimateId] || undefined,
      tags: this.parseTagsAttr(row[ROW.tags]),
      createdBy: row[ROW.createdBy] ?? '',
      updatedAt: row[ROW.updatedAt] ?? '',
      deleted: row[ROW.deleted] === 'true',
      // Sprint Board fields (defaulted for legacy rows without them).
      type: isTaskType(typeRaw) ? typeRaw : 'Task',
      sprintId: sprintIdRaw ? sprintIdRaw : null,
      boardStatus: isBoardStatus(boardStatusRaw) ? boardStatusRaw : 'TODO',
      blocked: row[ROW.blocked] === 'true',
      blocker: this.parseBlockerAttr(row[ROW.blocker]),
      storyPoints: this.parseNumberAttr(row[ROW.storyPoints]),
      effortDays: this.parseNumberAttr(row[ROW.effortDays]),
      confidence: this.parseNumberAttr(row[ROW.confidence]),
      goalId: row[ROW.goalId] || undefined,
      workstreamId: row[ROW.workstreamId] || undefined,
      serviceId: row[ROW.serviceId] || undefined,
      owner: row[ROW.owner] || undefined,
      context: row[ROW.context] || undefined,
      parentTaskId: row[ROW.parentTaskId] || undefined,
      actualEffortDays: this.parseNumberAttr(row[ROW.actualEffortDays]),
      completionNote: row[ROW.completionNote] || undefined,
      // Legacy rows without the attribute read back as false / undefined.
      adhoc: row[ROW.adhoc] === 'true',
      addedToSprintAt: row[ROW.addedToSprintAt] || undefined,
    };
  }

  /**
   * Lists a room's non-deleted tasks. Issues a roomId-filtered listMetaInfo
   * query, then re-filters client-side (collection + roomId + not-deleted) so
   * the result is correct even if the backend ignores the filter expression.
   */
  async listByRoom(roomId: string): Promise<Task[]> {
    if (!roomId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#roomId = :roomId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#roomId': ROW.roomId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':roomId': roomId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASKS_COLLECTION &&
          r[ROW.roomId] === roomId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toTask(r));
  }

  /** Loads a single task document by id (hydrates the S3 body). */
  async get(id: string): Promise<Task | undefined> {
    return this.docs.get(id);
  }

  /**
   * Lists a single sprint's non-deleted tasks. Issues a sprintId-filtered
   * listMetaInfo query (sprintId is mirrored as an index attribute), then
   * re-filters client-side (collection + sprintId + not-deleted) so the result
   * is correct even if the backend ignores the filter expression.
   */
  async listBySprint(sprintId: string): Promise<Task[]> {
    if (!sprintId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#sprintId = :sprintId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#sprintId': ROW.sprintId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':sprintId': sprintId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASKS_COLLECTION &&
          r[ROW.sprintId] === sprintId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toTask(r));
  }

  /**
   * Lists a room's backlog: non-deleted tasks with no sprint assignment. Reuses
   * the roomId-filtered query and re-filters for the backlog sentinel.
   */
  async listBacklogByRoom(roomId: string): Promise<Task[]> {
    const tasks = await this.listByRoom(roomId);
    return tasks.filter((t) => !t.sprintId);
  }

  /**
   * Lists a task's non-deleted SUBTASKS: tasks whose parentTaskId points at the
   * given parent id. Issues a parentTaskId-filtered listMetaInfo query
   * (parentTaskId is mirrored as an index attribute), then re-filters
   * client-side (collection + parentTaskId + not-deleted) so the result is
   * correct even if the backend ignores the filter expression.
   */
  async listByParent(parentId: string): Promise<Task[]> {
    if (!parentId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#parentTaskId = :parentTaskId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#parentTaskId': ROW.parentTaskId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':parentTaskId': parentId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASKS_COLLECTION &&
          r[ROW.parentTaskId] === parentId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toTask(r));
  }

  /**
   * Lists ALL non-deleted tasks across every room. Used by cross-room views
   * (e.g. the Dashboard) that aggregate counts/activity independent of the
   * active room. Filters to this collection + not-deleted defensively.
   */
  async listAll(): Promise<Task[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASKS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toTask(r));
  }

  /**
   * Creates a new task in a room. Seeds id/createdBy (from identity)/updatedAt
   * and returns the saved document.
   */
  async create(roomId: string, draft: TaskDraft): Promise<Task> {
    const identity = await this.api.identityProvider.getIdentity();
    const task: Task = {
      id: taskDocId(),
      roomId,
      title: draft.title.trim(),
      description: draft.description ?? '',
      status: draft.status,
      assignee: draft.assignee?.trim() || undefined,
      priority: draft.priority,
      estimateId: draft.estimateId?.trim() || undefined,
      tags: draft.tags ?? [],
      createdBy: identity.alias,
      updatedAt: new Date().toISOString(),
      deleted: false,
      parentTaskId: draft.parentTaskId?.trim() || undefined,
      ...this.sprintFieldsFromDraft(draft),
    };
    await this.docs.save(task.id, task, this.indexAttributes(task));
    return task;
  }

  /**
   * Projects the Sprint Board fields out of a draft, applying defaults (type →
   * 'Task', sprintId → backlog/null, boardStatus → 'TODO', blocked → false).
   * Kept in one place so create() and applyDraft() stay consistent.
   */
  private sprintFieldsFromDraft(
    draft: TaskDraft,
    base?: Task,
  ): Pick<
    Task,
    | 'type'
    | 'sprintId'
    | 'boardStatus'
    | 'blocked'
    | 'blocker'
    | 'storyPoints'
    | 'effortDays'
    | 'confidence'
    | 'goalId'
    | 'workstreamId'
    | 'serviceId'
    | 'owner'
    | 'context'
    | 'actualEffortDays'
    | 'completionNote'
    | 'adhoc'
    | 'addedToSprintAt'
  > {
    const blocked = draft.blocked ?? base?.blocked ?? false;
    const sprintId =
      draft.sprintId !== undefined ? draft.sprintId : base?.sprintId ?? null;
    return {
      type: draft.type ?? base?.type ?? 'Task',
      sprintId,
      boardStatus: draft.boardStatus ?? base?.boardStatus ?? 'TODO',
      blocked,
      blocker: blocked ? draft.blocker ?? base?.blocker : undefined,
      storyPoints: draft.storyPoints ?? base?.storyPoints,
      effortDays: draft.effortDays ?? base?.effortDays,
      confidence: draft.confidence ?? base?.confidence,
      goalId: (draft.goalId ?? base?.goalId)?.trim() || undefined,
      workstreamId: (draft.workstreamId ?? base?.workstreamId)?.trim() || undefined,
      serviceId: (draft.serviceId ?? base?.serviceId)?.trim() || undefined,
      owner: (draft.owner ?? base?.owner)?.trim() || undefined,
      context: (draft.context ?? base?.context)?.trim() || undefined,
      actualEffortDays: draft.actualEffortDays ?? base?.actualEffortDays,
      completionNote:
        (draft.completionNote ?? base?.completionNote)?.trim() || undefined,
      adhoc: draft.adhoc ?? base?.adhoc ?? false,
      addedToSprintAt:
        (draft.addedToSprintAt ?? base?.addedToSprintAt) || undefined,
    };
  }

  /**
   * Applies a draft's editable fields to an existing task in place (same id/
   * roomId/createdBy), refreshes updatedAt, and re-mirrors index attributes.
   */
  async applyDraft(task: Task, draft: TaskDraft): Promise<Task> {
    const updated: Task = {
      ...task,
      title: draft.title.trim(),
      description: draft.description ?? '',
      status: draft.status,
      assignee: draft.assignee?.trim() || undefined,
      priority: draft.priority,
      estimateId: draft.estimateId?.trim() || undefined,
      tags: draft.tags ?? [],
      updatedAt: new Date().toISOString(),
      deleted: false,
      ...this.sprintFieldsFromDraft(draft, task),
    };
    return this.update(updated);
  }

  /** Persists a full task document in place. */
  async update(task: Task): Promise<Task> {
    const updated: Task = { ...task, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Moves a task to a new status (Kanban column change). No-op-safe: returns a
   * task with the new status stamped and persisted.
   */
  async updateStatus(task: Task, status: Task['status']): Promise<Task> {
    if (task.status === status) return task;
    return this.update({
      ...task,
      status,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Moves a task to a new Sprint Board lane (TODO / IN PROGRESS / DONE). This is
   * independent of the legacy {@link Task.status}; the Sprint Board reads
   * {@link Task.boardStatus}.
   */
  async moveToBoardStatus(task: Task, boardStatus: BoardStatus): Promise<Task> {
    if (task.boardStatus === boardStatus) return task;
    return this.update({
      ...task,
      boardStatus,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Assigns a task to a sprint (or, with `null`, returns it to the backlog).
   * Sprint-planning drag/drop uses this.
   */
  async assignToSprint(task: Task, sprintId: string | null): Promise<Task> {
    if ((task.sprintId ?? null) === (sprintId ?? null)) return task;
    return this.update({
      ...task,
      sprintId: sprintId ?? null,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Adds a task to a sprint, optionally flagging it as unplanned/ad-hoc
   * (mid-sprint scope). When `markAdhoc` is true, sets {@link Task.adhoc} and
   * stamps {@link Task.addedToSprintAt} so sprint scope-change metrics can
   * attribute the added work — all in a single write. When false, sprint
   * membership flips but any existing adhoc flag is preserved.
   */
  async addToSprint(
    task: Task,
    sprintId: string,
    markAdhoc = false,
  ): Promise<Task> {
    const now = new Date().toISOString();
    return this.update({
      ...task,
      sprintId,
      adhoc: markAdhoc ? true : task.adhoc ?? false,
      addedToSprintAt: markAdhoc ? now : task.addedToSprintAt,
      updatedAt: now,
    });
  }

  /**
   * Sets or clears a task's unplanned/ad-hoc flag. Stamps
   * {@link Task.addedToSprintAt} (if not already set) when flagging, and leaves
   * the timestamp in place when clearing so history is not lost.
   */
  async setAdhoc(task: Task, adhoc: boolean): Promise<Task> {
    if ((task.adhoc ?? false) === adhoc) return task;
    const now = new Date().toISOString();
    return this.update({
      ...task,
      adhoc,
      addedToSprintAt: adhoc ? task.addedToSprintAt ?? now : task.addedToSprintAt,
      updatedAt: now,
    });
  }

  /**
   * Assigns (or, with an empty string, clears) a task's OWNER — the sprint-view
   * "assign later" path used by the board card menu and the task page. Mirrors
   * the owner onto {@link Task.assignee} so the existing assignee-based board
   * filters and By-Person grouping stay consistent. No-op-safe.
   */
  async assignOwner(task: Task, owner: string): Promise<Task> {
    const trimmed = owner.trim();
    if ((task.owner ?? '') === trimmed) return task;
    return this.update({
      ...task,
      owner: trimmed || undefined,
      assignee: trimmed || undefined,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Sets (or, with `undefined`, clears) a task's custom effort in ideal days.
   * Negative/non-finite values are treated as cleared. No-op-safe.
   */
  async setEffortDays(task: Task, effortDays: number | undefined): Promise<Task> {
    const next =
      typeof effortDays === 'number' && Number.isFinite(effortDays) && effortDays >= 0
        ? effortDays
        : undefined;
    if ((task.effortDays ?? undefined) === next) return task;
    return this.update({
      ...task,
      effortDays: next,
      updatedAt: new Date().toISOString(),
    });
  }

  /** Sets or clears a task's blocked state (and its structured blocker). */
  async setBlocked(
    task: Task,
    blocked: boolean,
    blocker?: Blocker,
  ): Promise<Task> {
    return this.update({
      ...task,
      blocked,
      blocker: blocked ? blocker ?? task.blocker : undefined,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Completes a task: moves it to DONE, clears any block, and captures the
   * actual effort (and optional completion note) that the Learn phase feeds
   * back into historical estimation accuracy.
   */
  async complete(
    task: Task,
    actualEffortDays: number,
    completionNote?: string,
  ): Promise<Task> {
    return this.update({
      ...task,
      boardStatus: 'DONE',
      blocked: false,
      blocker: undefined,
      actualEffortDays,
      completionNote: completionNote?.trim() || undefined,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Generates an AI 'Knowledge Base Research' answer for a task and PERSISTS it
   * on the task's {@link Task.kbResearch}. Grounds the enriched question against
   * the team knowledge base via Bedrock RetrieveAndGenerate, parses the
   * citations with the SAME helper the chat feature uses
   * ({@link KnowledgeBaseApi.parseCitations}), and writes the result through
   * {@link update} — so it lands in the S3 body ONLY (kbResearch is never
   * mirrored onto the pointer row). Throws on KB/model failure; the hook
   * surfaces it as an inline message and the task is otherwise unaffected.
   */
  async researchAndSave(task: Task, args: ResearchTaskArgs): Promise<Task> {
    const response = await this.api.knowledgeBase.retrieveAndGenerate({
      query: args.question,
    });
    const kbResearch: KbResearch = {
      question: args.question,
      markdown: (response.answer ?? '').trim(),
      citations: KnowledgeBaseApi.parseCitations(response.citations),
      generatedAt: new Date().toISOString(),
    };
    return this.update({
      ...task,
      kbResearch,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Soft-deletes a task: fetches it, flags it deleted, and updates both the S3
   * body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Task = {
      ...existing,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
